import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BadgePill from "@/components/BadgePill";
import NicknameCard from "@/components/NicknameCard";
import MessagesPanel from "@/components/MessagesPanel";
import SalesCard from "@/components/SalesCard";
import { WeekSchedule } from "@/components/WeekSchedule";
import { PhoneVerifyCard } from "@/components/PhoneVerifyCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Star, DollarSign, Banknote, Gift, Upload, ShieldCheck, CalendarOff, CalendarClock, Phone, Award, AlertTriangle, FileText } from "lucide-react";
import MilitaryVerification from "@/components/MilitaryVerification";
import { WEEKDAYS } from "@/lib/constants";

const defaultAvailability = () =>
  WEEKDAYS.reduce((acc, d) => ({
    ...acc,
    [d.key]: {
      enabled: ["monday", "tuesday", "wednesday", "thursday", "friday"].includes(d.key),
      start: "09:00", end: "17:00",
    },
  }), {});

export default function ProfessionalDashboard() {
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState({ categories: {} });
  const [application, setApplication] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [earnings, setEarnings] = useState({ total_payout: 0, paid_bookings: 0 });
  const [connect, setConnect] = useState({ connected: false, charges_enabled: false });
  const [connecting, setConnecting] = useState(false);
  const [cashappTag, setCashappTag] = useState(user.cashapp_tag || "");
  const [timeOff, setTimeOff] = useState(user.time_off || []);
  const [newDayOff, setNewDayOff] = useState("");
  const [req, setReq] = useState({ dates: [], newDate: "", category: "personal", reason: "", note: null });
  const [myRequests, setMyRequests] = useState([]);

  const loadRequests = () => {
    api.get("/professional/time-off-requests").then(({ data }) => setMyRequests(data)).catch(() => {});
  };
  const addReqDate = () => {
    if (!req.newDate || req.dates.includes(req.newDate)) { setReq({ ...req, newDate: "" }); return; }
    setReq({ ...req, dates: [...req.dates, req.newDate].sort(), newDate: "" });
  };
  const submitRequest = async () => {
    if (req.dates.length === 0) return toast.error("Add at least one day off");
    if (!req.reason.trim()) return toast.error("Please add a reason for your time off");
    const fd = new FormData();
    fd.append("dates", JSON.stringify(req.dates));
    fd.append("category", req.category);
    fd.append("reason", req.reason);
    if (req.note) fd.append("file", req.note);
    try {
      const { data } = await api.post("/professional/time-off-request", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message);
      setReq({ dates: [], newDate: "", category: "personal", reason: "", note: null });
      loadRequests();
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addDayOff = () => {
    if (!newDayOff) return;
    if (timeOff.includes(newDayOff)) { setNewDayOff(""); return; }
    setTimeOff([...timeOff, newDayOff].sort());
    setNewDayOff("");
  };
  const removeDayOff = (d) => setTimeOff(timeOff.filter((x) => x !== d));
  const saveTimeOff = async () => {
    try {
      const { data } = await api.put("/professional/time-off", { dates: timeOff });
      setTimeOff(data.time_off);
      toast.success("Time off saved");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const fmtDay = (d) => {
    try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }); }
    catch { return d; }
  };

  const saveCashapp = async () => {
    try {
      const { data } = await api.put("/professional/cashapp", { cashapp_tag: cashappTag });
      setCashappTag(data.cashapp_tag);
      toast.success(data.cashapp_tag ? `Cash App tag saved: ${data.cashapp_tag}` : "Cash App tag removed");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [form, setForm] = useState({
    references: "", why_fit: "", bio: "", phone: "",
    experience_details: "",
  });
  const [experienceTypes, setExperienceTypes] = useState([]);
  const [workExperiences, setWorkExperiences] = useState([]);
  const [newExp, setNewExp] = useState({ job_title: "", location: "", years: "" });
  const [promise, setPromise] = useState(false);
  const [contactConsent, setContactConsent] = useState(false);
  const [noBadApples, setNoBadApples] = useState(false);
  const [footCovers, setFootCovers] = useState(false);
  const [interviewAvailable, setInterviewAvailable] = useState(false);
  const [services, setServices] = useState([]);
  const [newSvc, setNewSvc] = useState({ name: "", category: "", price: "", description: "" });
  const [idUploading, setIdUploading] = useState(false);
  const [availability, setAvailability] = useState(defaultAvailability());
  const [certs, setCerts] = useState([]);
  const [newCert, setNewCert] = useState({ name: "", file: null });
  const [certUploading, setCertUploading] = useState(false);

  const loadCerts = () => {
    api.get("/professional/certifications").then(({ data }) => setCerts(data || [])).catch(() => {});
  };
  const addCert = async () => {
    if (!newCert.name || newCert.name.trim().length < 2) return toast.error("Enter the certification name");
    const fd = new FormData();
    fd.append("name", newCert.name.trim());
    if (newCert.file) fd.append("file", newCert.file);
    setCertUploading(true);
    try {
      await api.post("/professional/certifications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certification added");
      setNewCert({ name: "", file: null });
      loadCerts();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setCertUploading(false); }
  };
  const removeCert = async (id) => {
    try {
      await api.delete(`/professional/certifications/${id}`);
      loadCerts();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const [expDocs, setExpDocs] = useState([]);
  const [newDoc, setNewDoc] = useState({ label: "", file: null });
  const [docUploading, setDocUploading] = useState(false);
  const loadExpDocs = () => {
    api.get("/professional/experience-docs").then(({ data }) => setExpDocs(data || [])).catch(() => {});
  };
  const addExpDoc = async () => {
    if (!newDoc.file) return toast.error("Choose a document to upload");
    const fd = new FormData();
    fd.append("label", newDoc.label.trim());
    fd.append("file", newDoc.file);
    setDocUploading(true);
    try {
      await api.post("/professional/experience-docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Supporting document uploaded");
      setNewDoc({ label: "", file: null });
      loadExpDocs();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDocUploading(false); }
  };
  const removeExpDoc = async (id) => {
    try {
      await api.delete(`/professional/experience-docs/${id}`);
      loadExpDocs();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const setDay = (key, patch) =>
    setAvailability((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const uploadId = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setIdUploading(true);
    try {
      await api.post("/professional/id-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("ID uploaded securely");
      await refresh();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setIdUploading(false); }
  };

  const [resumeUploading, setResumeUploading] = useState(false);
  const uploadResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setResumeUploading(true);
    try {
      await api.post("/professional/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume uploaded");
      await refresh();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setResumeUploading(false); }
  };
  const removeResume = async () => {
    try {
      await api.delete("/professional/resume");
      toast.success("Resume removed");
      await refresh();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const load = () => {
    api.get("/catalog").then(({ data }) => setCatalog(data));
    api.get("/professional/application").then(({ data }) => {
      setApplication(data);
      if (data) {
        setServices(data.services || []);
        if (data.experience_types) setExperienceTypes(data.experience_types);
        if (data.work_experiences) setWorkExperiences(data.work_experiences);
        if (data.experience_details) setForm((f) => ({ ...f, experience_details: data.experience_details }));
        if (data.references) setForm((f) => ({ ...f, references: data.references }));
        if (data.why_fit) setForm((f) => ({ ...f, why_fit: data.why_fit }));
        if (data.availability && Object.keys(data.availability).length)
          setAvailability({ ...defaultAvailability(), ...data.availability });
      }
    }).catch(() => {});
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/professional/earnings").then(({ data }) => setEarnings(data)).catch(() => {});
  };
  const loadConnect = () => {
    api.get("/professional/connect/status").then(({ data }) => setConnect(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    loadConnect();
    loadRequests();
    loadCerts();
    loadExpDocs();
    loadStrikes();
    loadReviews();
    const params = new URLSearchParams(window.location.search);
    if (params.get("payouts")) {
      if (params.get("payouts") === "return") toast.success("Verifying your bank connection…");
      loadConnect();
      window.history.replaceState({}, "", "/dashboard");
    }
  }, []);

  const addService = () => {
    if (!newSvc.name || !newSvc.category || !newSvc.price) return toast.error("Fill service name, category and price");
    if (!newSvc.description.trim()) return toast.error("Add a short description for this service");
    setServices([...services, { name: newSvc.name, category: newSvc.category, price: parseFloat(newSvc.price), description: newSvc.description.trim() }]);
    setNewSvc({ name: "", category: "", price: "", description: "" });
  };

  const toggleExpType = (t) =>
    setExperienceTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);

  const addExperience = () => {
    if (!newExp.job_title.trim() || !newExp.location.trim() || !newExp.years)
      return toast.error("Enter job title, location and years on the job");
    const yrs = parseFloat(newExp.years);
    if (!(yrs > 0)) return toast.error("Years on the job must be greater than 0");
    setWorkExperiences([...workExperiences, { job_title: newExp.job_title.trim(), location: newExp.location.trim(), years: yrs }]);
    setNewExp({ job_title: "", location: "", years: "" });
  };
  const totalYears = workExperiences.reduce((s, e) => s + (e.years || 0), 0);

  const [interviewChoice, setInterviewChoice] = useState("");
  const [strikes, setStrikes] = useState(null);
  const loadStrikes = () => {
    api.get("/professional/strikes").then(({ data }) => setStrikes(data)).catch(() => {});
  };
  const [myReviews, setMyReviews] = useState([]);
  const [reportOpenId, setReportOpenId] = useState(null);
  const [reportForm, setReportForm] = useState({ issue: "", severity: "minor", score: "6" });
  const submitHomeReport = async (bid) => {
    if (reportForm.issue.trim().length < 5) return toast.error("Add a short note about the issue (5+ characters)");
    const sc = parseInt(reportForm.score);
    if (!(sc >= 1 && sc <= 10)) return toast.error("Pick a home score from 1 to 10");
    try {
      await api.post(`/bookings/${bid}/home-report`, { issue: reportForm.issue.trim(), severity: reportForm.severity, score: sc });
      toast.success("Sent privately to admin. The client won't see this.");
      setReportOpenId(null);
      setReportForm({ issue: "", severity: "minor", score: "6" });
      refresh();
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [cleanOpenId, setCleanOpenId] = useState(null);
  const [cleanNote, setCleanNote] = useState("");
  const [cleanFiles, setCleanFiles] = useState([]);
  const submitCleanReport = async (bid) => {
    if (!cleanFiles.length) return toast.error("Add at least 1 photo of the spotless home");
    if (cleanFiles.length > 5) return toast.error("Max 5 photos");
    const fd = new FormData();
    fd.append("note", cleanNote.trim());
    cleanFiles.forEach((f) => fd.append("photos", f));
    try {
      await api.post(`/bookings/${bid}/clean-report`, fd);
      toast.success("Clean report with photos sent to the administrator — thank you!");
      setCleanOpenId(null); setCleanNote(""); setCleanFiles([]);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const thankReview = async (rid) => {
    try {
      await api.post(`/professional/reviews/${rid}/thank`);
      toast.success("Thank-you sent! Your client got a text + 10% off their next booking with you 💝");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [flagOpenId, setFlagOpenId] = useState(null);
  const [flagReason, setFlagReason] = useState("");
  const loadReviews = () => {
    api.get("/professional/reviews").then(({ data }) => setMyReviews(data)).catch(() => {});
  };
  const submitFlag = async (reviewId) => {
    if (flagReason.trim().length < 10)
      return toast.error("Please give a short reason (10+ characters) why this review is unfair");
    try {
      await api.post(`/professional/reviews/${reviewId}/flag`, { reason: flagReason.trim() });
      toast.success("Flagged for admin review. It's now hidden while we look into it.");
      setFlagOpenId(null);
      setFlagReason("");
      loadReviews();
      loadStrikes();
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [coachChoice, setCoachChoice] = useState("");
  const selectCoaching = async () => {
    if (!coachChoice) return toast.error("Please pick a time");
    try {
      await api.post("/professional/coaching/select", { slot: coachChoice });
      toast.success("Coaching call confirmed! Check your email.");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const fmtSlot = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
  };
  const selectInterview = async () => {
    if (!interviewChoice) return toast.error("Please pick a time");
    try {
      await api.post("/professional/interview/select", { slot: interviewChoice });
      toast.success("Interview time confirmed! Check your email.");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!user.has_id) return toast.error("Please upload a photo of your driver's license or state ID first");
    if (workExperiences.length === 0) return toast.error("Add at least one experience (job title, location, years)");
    if (totalYears < 4) return toast.error("At least 4 years of combined experience required");
    if (expDocs.length === 0) return toast.error("Upload at least one supporting document proving your past 4 years of experience");
    if (!form.phone || form.phone.trim().length < 7) return toast.error("Please provide a valid phone number");
    if (!contactConsent) return toast.error("You must consent to receive texts/calls about hiring");
    if (!promise) return toast.error("You must agree to always be polite and wear a smile");
    if (!noBadApples) return toast.error("You must agree to the NO BAD APPLES pledge to apply");
    if (!footCovers) return toast.error("You must agree to always wear foot covers at each visit");
    if (experienceTypes.length === 0) return toast.error("Select where your 4 years of experience comes from (work, school, military)");
    if (form.experience_details.trim().length < 30) return toast.error("Please detail your 4 years of relevant experience (min 30 characters)");
    if (services.length === 0) return toast.error("Add at least one service");
    if (services.some((s) => !s.description)) return toast.error("Every service needs a description");
    if (!Object.values(availability).some((d) => d.enabled)) return toast.error("Select at least one day and the hours you're available");
    try {
      await api.post("/professional/apply", {
        work_experiences: workExperiences,
        references: form.references,
        why_fit: form.why_fit, bio: form.bio, services,
        experience_types: experienceTypes, experience_details: form.experience_details.trim(),
        phone: form.phone.trim(), contact_consent: contactConsent,
        availability,
        interview_available: interviewAvailable,
        no_bad_apples_agreed: noBadApples,
        foot_covers_agreed: footCovers,
      });
      toast.success("Application submitted for admin review!");
      load(); refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const startConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/professional/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setConnecting(false);
    }
  };

  const [agreeing, setAgreeing] = useState(false);
  const [scorecard, setScorecard] = useState(null);
  const [myWin, setMyWin] = useState(null);
  const [scReplies, setScReplies] = useState({});
  const [scReplyBusy, setScReplyBusy] = useState(null);
  const loadScorecard = () => api.get("/professional/qc-scorecard").then(({ data }) => setScorecard(data)).catch(() => {});
  useEffect(() => {
    if (user?.approved) loadScorecard();
    if (user?.approved) {
      api.get("/featured-winners").then(({ data }) => {
        const mine = (data.winners || []).filter((w) => w.provider_id === user.id);
        if (mine.length) setMyWin(mine[0]);
      }).catch(() => {});
    }
  }, [user?.approved]);

  const sendQcReply = async (checkId) => {
    const text = (scReplies[checkId] || "").trim();
    if (!text) { toast.error("Write a short thank-you first"); return; }
    setScReplyBusy(checkId);
    try {
      await api.post(`/professional/qc-checks/${checkId}/reply`, { reply: text });
      toast.success("Thank-you sent — the administrator will review it before it reaches the client");
      setScReplies((r) => { const c = { ...r }; delete c[checkId]; return c; });
      loadScorecard();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setScReplyBusy(null); }
  };
  const viewStandards = async () => {
    try {
      const res = await api.get("/professional/standards-doc", { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("The standards PDF isn't available yet — please contact support"); }
  };
  const agreeStandards = async () => {
    setAgreeing(true);
    try {
      await api.post("/professional/standards-ack");
      toast.success("Thank you! Standards accepted — you're ready to receive bookings 🎉");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAgreeing(false); }
  };

  const catNames = Object.keys(catalog.categories);
  const inputCls = "mt-1 bg-white";
  const mustApply = !user.approved && !application;

  return (
    <div className="space-y-8">
      {user.approved && !user.standards_ack?.agreed && (
        <div className="card-metal p-6 border-2 border-[#9b111e]/40" data-testid="standards-ack-card">
          <h2 className="font-heading font-black text-lg text-slate-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#9b111e]" /> One last step — agree to our Company Standards
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Before you can accept your first booking, please read the <b>Care-4U New Hire Standards</b> (dress,
            speech &amp; service standards) and tap <b>I Agree</b>. Clients can't book you until this is done.
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            <button onClick={viewStandards} className="btn-silver-3d !py-2 !px-4" data-testid="view-standards-btn">
              <FileText className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm">Read the Standards PDF</span>
            </button>
            <button onClick={agreeStandards} disabled={agreeing} className="btn-red-3d !py-2 !px-5" data-testid="agree-standards-btn">
              {agreeing ? "Saving…" : "I Agree to the Standards ✓"}
            </button>
          </div>
        </div>
      )}
      {myWin && (
        <div className="card-metal p-5 border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-white flex flex-wrap items-center justify-between gap-3" data-testid="winner-banner">
          <div>
            <h2 className="font-heading font-black text-lg text-slate-800">🏆 Featured Pro of the Week{myWin.streak > 1 ? ` — 🔥 ${myWin.streak}-week streak!` : "!"}</h2>
            <p className="text-sm text-slate-600 mt-0.5">
              Week of {new Date(myWin.week_start + "T00:00:00").toLocaleDateString()} · rated {myWin.week_rating}/10 by your clients.
              Grab your ready-made congrats image and post it to Facebook or Instagram!
            </p>
          </div>
          <a href={`${API}/winner-card/${myWin.id}`} target="_blank" rel="noreferrer"
            className="btn-red-3d !py-2 !px-4 text-sm flex-shrink-0" data-testid="winner-card-download-btn">
            🖼️ Get my shareable card
          </a>
        </div>
      )}
      {user.approved && scorecard && scorecard.total_checks > 0 && (
        <div className="card-metal p-6" data-testid="qc-scorecard">
          <h2 className="font-heading font-black text-lg text-slate-800 flex items-center gap-2">
            🔍 My QC Scorecard
            <span className="text-xs font-bold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">
              {scorecard.clean_checks}/{scorecard.total_checks} clean checks
            </span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Random quality checks clients answered about your visits — knock within 5 min, dress code + foot covers,
            CARE-4U lanyard, nickname greeting.
          </p>
          {scorecard.active_strikes > 0 && (
            <div className="mt-3 rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="qc-redemption-progress">
              <p className="text-sm font-bold text-amber-800">
                ⚠️ {scorecard.active_strikes} active QC strike{scorecard.active_strikes === 1 ? "" : "s"} — Redemption path:
                pass your next {scorecard.passes_needed} checks cleanly to clear one.
              </p>
              <div className="flex items-center gap-2 mt-2">
                {Array.from({ length: scorecard.passes_needed }).map((_, i) => (
                  <div key={i} className={`h-2.5 flex-1 rounded-full ${i < scorecard.clean_passes ? "bg-green-500" : "bg-slate-200"}`} />
                ))}
                <span className="text-xs font-black text-slate-600">{scorecard.clean_passes}/{scorecard.passes_needed}</span>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-3" data-testid="qc-scorecard-rates">
            {[["on_time", "⏰ On time"], ["dress_code", "👔 Dress code"], ["lanyard", "🪪 Lanyard"], ["nickname_greeting", "👋 Nickname"]].map(([k, label]) => (
              scorecard.pass_rates[k] !== null && (
                <span key={k} className={`text-xs font-bold rounded-full px-3 py-1.5 border ${scorecard.pass_rates[k] >= 90 ? "text-green-700 bg-green-50 border-green-200" : scorecard.pass_rates[k] >= 70 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-red-700 bg-red-50 border-red-200"}`}>
                  {label}: {scorecard.pass_rates[k]}%
                </span>
              )
            ))}
          </div>
          <div className="space-y-2 mt-3 max-h-56 overflow-y-auto pr-1">
            {scorecard.checks.map((c) => (
              <div key={c.id} className="rounded-lg border border-slate-200 p-2.5" data-testid={`scorecard-row-${c.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <p className="font-bold text-slate-700">{c.service} <span className="font-normal text-slate-400 text-xs">· {new Date(c.completed_at).toLocaleDateString()}</span></p>
                  <div className="flex gap-1.5">
                    {[["on_time", "⏰"], ["dress_code", "👔"], ["lanyard", "🪪"], ["nickname_greeting", "👋"]].map(([k, ic]) => (
                      <span key={k} title={k} className={`text-xs font-black rounded-full w-6 h-6 flex items-center justify-center ${c.answers?.[k] ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {ic}
                      </span>
                    ))}
                  </div>
                </div>
                {c.note && (
                  <div className="mt-1.5 border-l-2 border-[#9b111e] pl-2" data-testid={`scorecard-note-${c.id}`}>
                    <p className="text-xs text-slate-600 italic">
                      💬 "{c.note}" <span className="not-italic text-slate-400">— client feedback (admin-reviewed)</span>
                    </p>
                    {!c.reply && (
                      <div className="flex gap-2 mt-1.5">
                        <input value={scReplies[c.id] || ""} onChange={(e) => setScReplies((r) => ({ ...r, [c.id]: e.target.value }))}
                          maxLength={300} placeholder="Send a short thank-you back (admin-reviewed first)…"
                          className="flex-1 text-xs bg-white border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#9b111e]"
                          data-testid={`qc-reply-input-${c.id}`} />
                        <button onClick={() => sendQcReply(c.id)} disabled={scReplyBusy === c.id}
                          className="text-xs font-bold text-white bg-[#9b111e] hover:bg-[#7d0e18] rounded-full px-3 py-1" data-testid={`qc-reply-send-${c.id}`}>
                          {scReplyBusy === c.id ? "…" : "Say thanks"}
                        </button>
                      </div>
                    )}
                    {c.reply && (
                      <p className="text-[11px] mt-1" data-testid={`qc-reply-status-${c.id}`}>
                        <span className="text-slate-500 italic">Your thank-you: "{c.reply}"</span>{" "}
                        {c.reply_status === "pending_admin" && <span className="font-bold text-amber-600">· awaiting admin review</span>}
                        {c.reply_status === "approved" && <span className="font-bold text-green-700">· ✓ delivered to the client</span>}
                        {c.reply_status === "rejected" && <span className="font-bold text-red-600">· ✗ not approved</span>}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* status + stats */}
      <div className="grid sm:grid-cols-4 gap-4">
        <div className="card-metal p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500">Status</p>
          <p className={`font-heading font-black text-lg ${user.approved ? "text-green-600" : "text-amber-600"}`} data-testid="pro-status">
            {user.approved ? "Vetted ✓" : application ? "Pending Review" : "Not Applied"}
          </p>
        </div>
        <div className="card-metal p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500">Badge</p>
          <div className="mt-1"><BadgePill tier={user.badge} testId="pro-badge" /></div>
          <p className="text-xs text-slate-400 mt-1">{user.points} pts</p>
        </div>
        <div className="card-metal p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500">Rating</p>
          <p className="font-heading font-black text-lg text-[#9b111e] flex items-center gap-1">
            <Star className="w-4 h-4 fill-[#9b111e]" /> {user.avg_rating || 0}/10
          </p>
        </div>
        <div className="card-metal p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500">Payout Earned</p>
          <p className="font-heading font-black text-lg text-green-700 flex items-center">
            <DollarSign className="w-4 h-4" />{earnings.total_payout}
          </p>
          <p className="text-xs text-[#9b111e] mt-1 flex items-center">
            <Gift className="w-3 h-3 mr-1" />${earnings.tips_total || 0} in tips ({earnings.tip_count || 0})
          </p>
        </div>
      </div>

      {/* NO BAD APPLES — Back-Up Pro status */}
      {user.backup_status && (
        <div className="card-metal p-6 border-2 border-[#9b111e] bg-red-50/70" data-testid="backup-status-banner">
          <h2 className="font-heading font-black text-lg flex items-center gap-2 text-[#9b111e]">
            <AlertTriangle className="w-5 h-5" /> You're in Back-Up Pro status
          </h2>
          <p className="text-sm text-slate-700 mt-1">
            Under our <b>NO BAD APPLES</b> guarantee, you're temporarily booking at a <b>20% discounted rate</b> —
            but <b>your tips still pay 100%!</b> Work EXTRA hard, put customer service first, and earn
            <b> 3 perfect 10/10 reviews</b> to be <b>INSTANTLY reinstated</b> to full price — or 6 great reviews (8 or better) works too.
          </p>
          <div className="mt-3">
            <div className="flex justify-between text-xs font-bold text-slate-600 mb-1">
              <span>⚡ Perfect 10/10s — instant reinstatement</span>
              <span data-testid="perfect-reinstate-progress">{user.perfect_reviews_in_backup || 0} / 3</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-[#9b111e]" style={{ width: `${Math.min(100, ((user.perfect_reviews_in_backup || 0) / 3) * 100)}%` }} />
            </div>
          </div>
          <div className="mt-3">
            <div className="flex justify-between text-xs font-bold text-slate-600 mb-1">
              <span>Great reviews toward reinstatement</span>
              <span data-testid="reinstate-progress">{user.great_reviews_in_backup || 0} / 6</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-green-500" style={{ width: `${Math.min(100, ((user.great_reviews_in_backup || 0) / 6) * 100)}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* strike history */}
      {strikes && user.approved && (
        <div className="card-metal p-6" data-testid="strike-history-card">
          <h2 className="font-heading font-black text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#9b111e]" /> Your NO BAD APPLES Record
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            {strikes.negative_points === 0 && !strikes.backup_status
              ? "Spotless record — keep it up! 🍎✨"
              : `${strikes.negative_points} of ${strikes.strikes_to_backup} strikes.`}
            {strikes.backup_status && ` You're in Back-Up status — ${strikes.perfect_reviews_in_backup || 0}/${strikes.perfect_reviews_needed || 3} perfect 10/10s (instant reinstatement) · ${strikes.great_reviews_in_backup}/${strikes.great_reviews_needed} great reviews.`}
          </p>
          {(strikes.log || []).length === 0 ? (
            <p className="text-sm text-slate-400 mt-3">No strikes on record. Every review of 8+ keeps you golden.</p>
          ) : (
            <div className="mt-3 space-y-2" data-testid="strike-log">
              {strikes.log.map((e) => {
                const neg = e.points < 0;
                const good = e.type === "great_review" || e.type === "reinstated" || e.type === "strike_reversed";
                return (
                  <div key={e.id} className={`flex items-start justify-between gap-3 rounded-lg border p-3 text-sm ${neg ? "border-red-200 bg-red-50" : good ? "border-green-200 bg-green-50" : "border-slate-200 bg-slate-50"}`} data-testid={`strike-entry-${e.id}`}>
                    <div>
                      <p className="font-semibold">
                        {e.type === "low_review" && `Low review (${e.rating}/10)`}
                        {e.type === "no_show" && "Client reported a no-show"}
                        {e.type === "great_review" && `Great review (${e.rating}/10) 🎉`}
                        {e.type === "backup" && "Moved to Back-Up Pro status"}
                        {e.type === "reinstated" && "Reinstated to full status ✅"}
                        {e.type === "strike_reversed" && "Strike reversed — flagged review removed ✅"}
                        {e.type === "message_violation" && "Message violation — suspended by admin"}
                      </p>
                      <p className="text-xs text-slate-500">
                        {e.service ? `${e.service} · ` : ""}{e.note || ""}
                        {e.when ? ` · ${new Date(e.when).toLocaleDateString()}` : ""}
                      </p>
                    </div>
                    {e.points !== 0 && (
                      <span className={`font-heading font-black flex-shrink-0 ${neg ? "text-red-600" : "text-green-600"}`}>
                        {e.points > 0 ? `+${e.points}` : e.points}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* my reviews + flag unfair */}
      {user.approved && myReviews.length > 0 && (
        <div className="card-metal p-6" data-testid="my-reviews-card">
          <h2 className="font-heading font-black text-lg flex items-center gap-2">
            <Star className="w-5 h-5 text-[#9b111e]" /> Your Reviews
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Think a review is unfair? Flag it and Rob will personally look into it — flagged reviews are hidden while under review.
          </p>
          <div className="mt-3 space-y-3" data-testid="my-reviews-list">
            {myReviews.map((r) => {
              const fstatus = r.flag?.status;
              return (
                <div key={r.id} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`review-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold flex items-center gap-2">
                        <span className={`font-heading font-black ${r.rating < 8 ? "text-red-600" : "text-green-600"}`}>{r.rating}/10</span>
                        <span className="text-slate-500">· {r.service}</span>
                      </p>
                      <p className="text-slate-600 mt-1">{r.comment || <span className="italic text-slate-400">No comment</span>}</p>
                      {r.photo_count > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap" data-testid={`pro-review-photos-${r.id}`}>
                          {(r.photo_idxs || []).map((i) => (
                            <a key={i} href={`${API}/reviews/${r.id}/photo/${i}`} target="_blank" rel="noreferrer">
                              <img src={`${API}/reviews/${r.id}/photo/${i}`} alt="Review" loading="lazy"
                                className="w-16 h-16 object-cover rounded-lg border border-slate-200 hover:opacity-90 transition-opacity" />
                            </a>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-1">— {r.client_name} · {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                      {r.rating >= 8 && !r.thanked && (
                        <button
                          className="text-xs font-semibold text-green-700 border border-green-400 rounded-full px-3 py-1 hover:bg-green-50 transition-colors"
                          onClick={() => thankReview(r.id)}
                          data-testid={`thank-review-btn-${r.id}`}>
                          💝 Thank & gift 10% off
                        </button>
                      )}
                      {r.thanked && (
                        <span className="text-xs font-semibold text-green-700 bg-green-50 rounded-full px-3 py-1" data-testid={`thanked-chip-${r.id}`}>
                          Thanked · 10% gifted 💝
                        </span>
                      )}
                      {!fstatus && (
                        <button
                          className="text-xs font-semibold text-[#9b111e] border border-[#9b111e]/40 rounded-full px-3 py-1 hover:bg-red-50 transition-colors"
                          onClick={() => { setFlagOpenId(flagOpenId === r.id ? null : r.id); setFlagReason(""); }}
                          data-testid={`flag-review-btn-${r.id}`}>
                          Flag as unfair
                        </button>
                      )}
                      {fstatus === "flagged" && (
                        <span className="text-xs font-semibold text-amber-600 bg-amber-50 rounded-full px-3 py-1" data-testid={`flag-status-${r.id}`}>
                          Under admin review{r.flag?.call?.status === "scheduled" ? " · call scheduled" : r.flag?.call?.status === "offered" ? " · call offered" : ""}
                        </span>
                      )}
                      {fstatus === "resolved_removed" && (
                        <span className="text-xs font-semibold text-green-700 bg-green-50 rounded-full px-3 py-1" data-testid={`flag-status-${r.id}`}>Removed ✓</span>
                      )}
                      {fstatus === "resolved_upheld" && (
                        <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-3 py-1" data-testid={`flag-status-${r.id}`}>Kept after review</span>
                      )}
                    </div>
                  </div>
                  {flagOpenId === r.id && (
                    <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`flag-form-${r.id}`}>
                      <Label className="text-xs">Why is this review unfair?</Label>
                      <Textarea
                        value={flagReason}
                        onChange={(e) => setFlagReason(e.target.value)}
                        placeholder="e.g. The client was upset I wouldn't do extra unpaid work, not about the service quality."
                        className="mt-1 text-sm"
                        data-testid={`flag-reason-input-${r.id}`} />
                      <div className="flex gap-2 mt-2">
                        <button className="btn-metal text-xs px-4 py-1.5" onClick={() => submitFlag(r.id)} data-testid={`flag-submit-${r.id}`}>Submit flag</button>
                        <button className="text-xs text-slate-500 px-3 py-1.5" onClick={() => { setFlagOpenId(null); setFlagReason(""); }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* coaching call offer */}
      {user.coaching?.status === "offered" && (
        <div className="card-metal p-6 border-2 border-blue-400" data-testid="coaching-offer-banner">
          <h2 className="font-heading font-black text-lg flex items-center gap-2 text-blue-700">
            <CalendarClock className="w-5 h-5" /> Coaching Call Requested
          </h2>
          <p className="text-sm text-slate-600 mt-1">The administrator wants to help you get back to full status. Pick a time:</p>
          <div className="mt-3 space-y-2">
            {(user.coaching.offered_slots || []).map((s) => (
              <label key={s} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${coachChoice === s ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`coaching-slot-${s}`}>
                <input type="radio" name="coaching-slot" value={s} checked={coachChoice === s} onChange={() => setCoachChoice(s)} className="accent-blue-600" />
                <span className="font-semibold text-slate-800">{fmtSlot(s)}</span>
              </label>
            ))}
          </div>
          <button onClick={selectCoaching} className="btn-red-3d mt-4" data-testid="confirm-coaching-btn">Confirm This Time</button>
        </div>
      )}
      {user.coaching?.status === "scheduled" && user.coaching?.selected && (
        <div className="card-metal p-5 border-2 border-green-400 flex items-center gap-3" data-testid="coaching-scheduled-banner">
          <Phone className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-slate-700"><b className="text-green-700">Coaching call scheduled</b> for <b>{fmtSlot(user.coaching.selected)}</b>. Rob will call you then.</p>
        </div>
      )}

      {/* interview scheduler */}
      {application?.interview_status === "offered" && (
        <div className="card-metal p-6 border-2 border-blue-400" data-testid="interview-offer-banner">
          <h2 className="font-heading font-black text-lg flex items-center gap-2 text-blue-700">
            <CalendarClock className="w-5 h-5" /> Phone Interview Requested
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            The Care-4U.net administrator would like a quick phone interview before approving you. Pick a time that works:
          </p>
          <div className="mt-3 space-y-2">
            {(application.interview_offered_slots || []).map((s) => (
              <label key={s} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${interviewChoice === s ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`interview-slot-${s}`}>
                <input type="radio" name="interview-slot" value={s} checked={interviewChoice === s}
                  onChange={() => setInterviewChoice(s)} className="accent-blue-600" />
                <span className="font-semibold text-slate-800">{fmtSlot(s)}</span>
              </label>
            ))}
          </div>
          <button onClick={selectInterview} className="btn-red-3d mt-4" data-testid="confirm-interview-btn">Confirm This Time</button>
        </div>
      )}
      {application?.interview_status === "scheduled" && application?.interview_selected && (
        <div className="card-metal p-5 border-2 border-green-400 flex items-center gap-3" data-testid="interview-scheduled-banner">
          <Phone className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-slate-700">
            <b className="text-green-700">Phone interview scheduled</b> for <b>{fmtSlot(application.interview_selected)}</b>.
            Rob will call the number on your application — keep an eye on your phone!
          </p>
        </div>
      )}

      {mustApply && (
        <div className="card-metal p-5 border-2 border-[#9b111e] bg-red-50/60" data-testid="must-apply-banner">
          <h2 className="font-heading font-black text-lg text-[#9b111e]">Complete your application to activate your account</h2>
          <p className="text-sm text-slate-600 mt-1">
            Creating an account isn't enough — please fill out your <b>4+ years of experience</b>, services and availability
            below. Your application is sent to the administrator for review before you can receive bookings.
          </p>
        </div>
      )}

      {!mustApply && (
      <>
      <div className="card-metal p-6" data-testid="payouts-card">
        <h2 className="font-heading font-black text-lg flex items-center gap-2 mb-3">
          <Banknote className="w-5 h-5 text-[#9b111e]" /> Bank Payouts (Stripe)
        </h2>
        {connect.charges_enabled ? (
          <div data-testid="payouts-connected">
            <p className="text-green-700 font-semibold flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Bank account connected — you're getting paid!
            </p>
            <p className="text-xs text-slate-500 mt-2">
              When a client pays, Stripe instantly deposits your <b>95% share</b> straight to your bank.
              Care-4U.net keeps a transparent 5% fee.
            </p>
            <button onClick={startConnect} disabled={connecting} className="btn-red-3d mt-3 text-sm" data-testid="manage-payouts-btn">
              {connecting ? "Opening…" : "Update Bank Details"}
            </button>
          </div>
        ) : connect.connected ? (
          <div data-testid="payouts-pending">
            <p className="text-amber-600 font-semibold">
              Almost there — finish your secure Stripe verification to start receiving payouts.
            </p>
            <button onClick={startConnect} disabled={connecting} className="btn-red-3d mt-3" data-testid="continue-payouts-btn">
              {connecting ? "Opening…" : "Finish Payout Setup"}
            </button>
          </div>
        ) : (
          <div data-testid="payouts-not-connected">
            <p className="text-sm text-slate-600 mb-3">
              Connect your bank account to receive automatic direct-deposit payouts. The moment a client pays,
              your <b>95% share</b> is sent straight to your bank — no invoices, no waiting.
            </p>
            <button onClick={startConnect} disabled={connecting} className="btn-red-3d" data-testid="connect-payouts-btn">
              {connecting ? "Opening…" : "Connect Payouts"}
            </button>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-3">🔒 Bank details are handled securely by Stripe with 256-bit encryption — Care-4U.net never sees or stores them.</p>
      </div>

      <MilitaryVerification />

      {/* Cash App tips */}
      <div className="card-metal p-6" data-testid="cashapp-tips-card">
        <h2 className="font-heading font-black text-lg flex items-center gap-2 mb-2">
          <Gift className="w-5 h-5 text-[#9b111e]" /> Cash App Tips (optional)
        </h2>
        <p className="text-sm text-slate-600 mb-3">
          Add your Cash App tag so clients can tip you <b>directly</b> — tips go straight to you, 100% yours, no fee.
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <Label>Your Cash App tag</Label>
            <Input className={inputCls} data-testid="cashapp-tag-input" placeholder="$yourtag"
              value={cashappTag} onChange={(e) => setCashappTag(e.target.value)} />
          </div>
          <button onClick={saveCashapp} className="btn-red-3d" data-testid="save-cashapp-btn">Save Cash App Tag</button>
        </div>
      </div>

      {/* time off / blocked dates */}
      <div className="card-metal p-6" data-testid="time-off-card">
        <h2 className="font-heading font-black text-lg flex items-center gap-2 mb-2">
          <CalendarOff className="w-5 h-5 text-[#9b111e]" /> Time Off / Blocked Dates
        </h2>
        <p className="text-sm text-slate-600 mb-3">
          Block specific dates (holidays, vacation, appointments) on top of your weekly hours. Clients won't be able to book you on these days.
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label>Add a date to block</Label>
            <Input type="date" className={inputCls + " w-52"} data-testid="time-off-date-input"
              value={newDayOff} onChange={(e) => setNewDayOff(e.target.value)} />
          </div>
          <button onClick={addDayOff} className="btn-silver-3d !py-2 !px-4" data-testid="add-time-off-btn">
            <Plus className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm">Add</span>
          </button>
          <button onClick={saveTimeOff} className="btn-red-3d" data-testid="save-time-off-btn">Save Time Off</button>
        </div>
        {timeOff.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="time-off-list">
            {timeOff.map((d) => (
              <span key={d} className="flex items-center gap-2 bg-red-50 border border-[#9b111e]/30 text-[#9b111e] rounded-full px-3 py-1 text-sm font-semibold" data-testid={`time-off-${d}`}>
                {fmtDay(d)}
                <button onClick={() => removeDayOff(d)} data-testid={`remove-time-off-${d}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400 mt-3">No blocked dates. You're bookable during all your weekly hours.</p>}
        <p className="text-xs text-slate-400 mt-3">Remember to hit <b>Save Time Off</b> after adding or removing dates.</p>
      </div>

      {/* monthly time-off request */}
      <div className="card-metal p-6" data-testid="time-off-request-card">
        {new Date().getDate() <= 5 && (
          <div className="mb-4 rounded-xl border border-[#9b111e]/30 bg-red-50 p-3 text-sm text-[#9b111e] font-semibold" data-testid="monthly-timeoff-banner">
            📅 It's the start of {new Date().toLocaleString(undefined, { month: "long" })} — please submit any days off you'll need this month.
          </div>
        )}
        <h2 className="font-heading font-black text-lg flex items-center gap-2 mb-1">
          <CalendarOff className="w-5 h-5 text-[#9b111e]" /> Request Days Off
        </h2>
        <p className="text-sm text-slate-600 mb-3">
          Submit the days off you need with a reason. <b>4+ days in a row</b> or <b>more than 5 days off in a month</b>
          need admin approval. <b>More than 5 sick days in a month</b> also requires a photo of a doctor's note.
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label>Pick a date</Label>
            <Input type="date" className={inputCls + " w-48"} data-testid="req-date-input"
              value={req.newDate} onChange={(e) => setReq({ ...req, newDate: e.target.value })} />
          </div>
          <button onClick={addReqDate} className="btn-silver-3d !py-2 !px-4" data-testid="req-add-date-btn">
            <Plus className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm">Add Day</span>
          </button>
          <div>
            <Label>Type</Label>
            <select className={inputCls + " w-40"} data-testid="req-category"
              value={req.category} onChange={(e) => setReq({ ...req, category: e.target.value })}>
              <option value="personal">Personal / Vacation</option>
              <option value="sick">Sick</option>
            </select>
          </div>
        </div>
        {req.dates.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="req-dates-list">
            {req.dates.map((d) => (
              <span key={d} className="flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-full px-3 py-1 text-sm">
                {d}
                <button onClick={() => setReq({ ...req, dates: req.dates.filter((x) => x !== d) })}><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>
              </span>
            ))}
          </div>
        )}
        <div className="mt-3">
          <Label>Reason</Label>
          <textarea rows={2} className={inputCls + " w-full"} data-testid="req-reason"
            placeholder="Why do you need these days off?" value={req.reason}
            onChange={(e) => setReq({ ...req, reason: e.target.value })} />
        </div>
        {req.category === "sick" && (
          <div className="mt-3">
            <Label>Doctor's note (required if more than 5 sick days this month)</Label>
            <input type="file" accept="image/*" className="mt-1 block text-sm" data-testid="req-doctor-note"
              onChange={(e) => setReq({ ...req, note: e.target.files?.[0] || null })} />
          </div>
        )}
        <button onClick={submitRequest} className="btn-red-3d mt-4" data-testid="submit-timeoff-request-btn">Submit Request</button>

        {myRequests.length > 0 && (
          <div className="mt-6" data-testid="my-timeoff-requests">
            <h3 className="font-heading font-black mb-2">My Requests</h3>
            <div className="space-y-2">
              {myRequests.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border border-slate-200 rounded-lg p-3 text-sm" data-testid={`my-request-${r.id}`}>
                  <div>
                    <span className="font-semibold capitalize">{r.category}</span> · {r.dates.length} day(s): {r.dates.join(", ")}
                    <div className="text-slate-500 text-xs">{r.reason}</div>
                  </div>
                  <span className={"badge-tier " + (r.status === "approved" ? "bg-green-100 text-green-700" : r.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700")}>
                    {r.status === "pending" ? "Awaiting Approval" : r.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      </>
      )}

      {/* application form */}
      {!user.approved && (
        <div className="card-metal p-6">
          <h2 className="font-heading font-black text-xl mb-4">Professional Application</h2>

          <div className="mb-5 rounded-xl border border-[#9b111e]/30 bg-red-50/60 p-4" data-testid="id-verification-block">
            <h3 className="font-heading font-black flex items-center gap-2 text-[#9b111e]">
              <ShieldCheck className="w-5 h-5" /> Identity Verification
            </h3>
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">
              Upload a <b>clear, well-lit, in-focus</b> photo of your driver's license or state ID.
              🔒 Your personal information will <b>never</b> be shared with third parties. Your ID is
              handled securely (never stored on this website), viewed <b>only once</b> by our administrator
              at onboarding to cross-reference the certifications you hold, and then <b>permanently deleted</b>.
              You'll receive an <b>email confirmation the moment your ID is deleted</b>.
            </p>
            <div className="flex items-center gap-3 mt-3">
              <label className="btn-silver-3d !py-2 !px-4 cursor-pointer">
                <Upload className="w-4 h-4 text-[#9b111e]" />
                <span className="text-engraved text-sm">{idUploading ? "Uploading…" : user.has_id ? "Replace ID" : "Upload ID Photo"}</span>
                <input type="file" accept="image/*" className="hidden" data-testid="id-upload-input"
                  onChange={uploadId} disabled={idUploading} />
              </label>
              {user.has_id && (
                <span className="text-green-600 text-sm font-bold flex items-center gap-1" data-testid="id-on-file">
                  <ShieldCheck className="w-4 h-4" /> ID securely uploaded ✓
                </span>
              )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Short Bio</Label><Input className={inputCls} data-testid="app-bio"
              value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></div>
            <div><Label>Phone Number</Label><Input type="tel" className={inputCls} data-testid="app-phone"
              placeholder="(555) 123-4567"
              value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          </div>

          {/* structured work experience */}
          <h3 className="font-heading font-black mt-6 mb-1">Your 4+ Years of Experience <span className="text-[#9b111e]">*</span></h3>
          <p className="text-sm text-slate-500 mb-3">
            Add each job, school or military role — with the <b>location</b>, your <b>job title</b>, and <b>years on the job</b>.
            You must total <b>at least 4 years</b> to apply. This is sent to the administrator for review.
          </p>
          <div className="grid sm:grid-cols-4 gap-2 items-end" data-testid="experience-builder">
            <div><Label>Job title</Label><Input className={inputCls} data-testid="exp-title"
              placeholder="e.g. Certified Nursing Assistant" value={newExp.job_title}
              onChange={(e) => setNewExp({ ...newExp, job_title: e.target.value })} /></div>
            <div><Label>Location</Label><Input className={inputCls} data-testid="exp-location"
              placeholder="e.g. Mercy Hospital, Austin TX" value={newExp.location}
              onChange={(e) => setNewExp({ ...newExp, location: e.target.value })} /></div>
            <div><Label>Years on job</Label><Input type="number" min="0" step="0.5" className={inputCls} data-testid="exp-years"
              placeholder="e.g. 3" value={newExp.years}
              onChange={(e) => setNewExp({ ...newExp, years: e.target.value })} /></div>
            <button onClick={addExperience} className="btn-silver-3d self-end" data-testid="add-exp-btn">
              <Plus className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm ml-1">Add</span>
            </button>
          </div>
          {workExperiences.length > 0 && (
            <div className="flex flex-col gap-2 mt-3" data-testid="experience-list">
              {workExperiences.map((e, i) => (
                <div key={i} className="flex items-center justify-between gap-2 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid={`exp-item-${i}`}>
                  <span><b className="font-semibold">{e.job_title}</b> · {e.location} <span className="text-slate-500">· {e.years} yr{e.years !== 1 ? "s" : ""}</span></span>
                  <button onClick={() => setWorkExperiences(workExperiences.filter((_, j) => j !== i))} data-testid={`remove-exp-${i}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              ))}
              <p className={`text-sm font-bold ${totalYears >= 5 ? "text-green-600" : "text-[#9b111e]"}`} data-testid="exp-total">
                Total: {totalYears} year{totalYears !== 1 ? "s" : ""} {totalYears >= 5 ? "✓" : `— need ${(5 - totalYears).toFixed(1)} more to apply`}
              </p>
            </div>
          )}

          {/* supporting documents (required) */}
          <h3 className="font-heading font-black mt-6 mb-1">Supporting Documents <span className="text-[#9b111e]">*</span></h3>
          <p className="text-sm text-slate-500 mb-3">
            Upload <b>one or more documents</b> that prove your past 4 years of work / school / military experience —
            pay stubs, employment letters, diplomas, transcripts, a DD-214, etc. These are sent to the administrator for approval.
          </p>
          <div className="grid sm:grid-cols-5 gap-2 items-end" data-testid="exp-doc-builder">
            <div className="sm:col-span-2"><Label>Label (optional)</Label>
              <Input className={inputCls} data-testid="exp-doc-label" placeholder="e.g. DD-214, Nursing Diploma"
                value={newDoc.label} onChange={(e) => setNewDoc({ ...newDoc, label: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Document (PDF or image)</Label>
              <input type="file" accept="application/pdf,image/*" className="mt-1 block text-sm w-full" data-testid="exp-doc-file"
                onChange={(e) => setNewDoc({ ...newDoc, file: e.target.files?.[0] || null })} /></div>
            <button onClick={addExpDoc} disabled={docUploading} className="btn-silver-3d self-end" data-testid="add-exp-doc-btn">
              <Plus className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm ml-1">{docUploading ? "Uploading…" : "Add"}</span>
            </button>
          </div>
          {expDocs.length > 0 && (
            <div className="flex flex-col gap-2 mt-3" data-testid="exp-doc-list">
              {expDocs.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid={`exp-doc-item-${d.id}`}>
                  <span className="flex items-center gap-2">
                    <Upload className="w-4 h-4 text-[#9b111e]" />
                    <span className="font-semibold">{d.label || d.filename}</span>
                    {d.label && <span className="text-xs text-slate-400">· {d.filename}</span>}
                  </span>
                  <button onClick={() => removeExpDoc(d.id)} data-testid={`remove-exp-doc-${d.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4"><Label>References</Label><Textarea className={inputCls} data-testid="app-references"
            value={form.references} onChange={(e) => setForm({ ...form, references: e.target.value })} /></div>
          <div className="mt-4"><Label>Why are you a good fit for Care-4U.net?</Label><Textarea className={inputCls} data-testid="app-whyfit"
            value={form.why_fit} onChange={(e) => setForm({ ...form, why_fit: e.target.value })} /></div>

          {/* experience package */}
          <h3 className="font-heading font-black mt-6 mb-1">Your Experience Package</h3>
          <p className="text-sm text-slate-500 mb-3">
            Tell us where your <b>4+ years of experience</b> in your field of expertise comes from, then detail it below.
          </p>
          <div className="flex flex-wrap gap-4" data-testid="experience-types">
            {[{ k: "work", label: "Work experience" }, { k: "school", label: "School / education" }, { k: "military", label: "Military service" }].map((t) => (
              <label key={t.k} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={experienceTypes.includes(t.k)} onCheckedChange={() => toggleExpType(t.k)} data-testid={`exp-type-${t.k}`} />
                <span className="font-semibold">{t.label}</span>
              </label>
            ))}
          </div>
          <div className="mt-3"><Label>Detail your 4 years of relevant experience (work / school / military)</Label>
            <Textarea className={inputCls} rows={4} data-testid="app-experience-details"
              placeholder="e.g. 6 yrs as a certified nursing assistant at St. Mary's; U.S. Army medic (2015–2019); AAS in Nursing…"
              value={form.experience_details} onChange={(e) => setForm({ ...form, experience_details: e.target.value })} /></div>

          {/* certifications */}
          <h3 className="font-heading font-black mt-6 mb-1">Certifications <span className="text-slate-400 font-normal text-sm">(optional)</span></h3>
          <p className="text-sm text-slate-500 mb-3">
            List any certifications, licenses or diplomas. You can attach a photo or PDF — it's viewed once by the
            administrator to verify, then <b>permanently deleted</b> (never shown publicly).
          </p>
          <div className="grid sm:grid-cols-5 gap-2 items-end" data-testid="cert-builder">
            <div className="sm:col-span-2"><Label>Certification name</Label>
              <Input className={inputCls} data-testid="cert-name" placeholder="e.g. Certified Nursing Assistant"
                value={newCert.name} onChange={(e) => setNewCert({ ...newCert, name: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Attach photo / PDF (optional)</Label>
              <input type="file" accept="image/*,application/pdf" className="mt-1 block text-sm w-full" data-testid="cert-file"
                onChange={(e) => setNewCert({ ...newCert, file: e.target.files?.[0] || null })} /></div>
            <button onClick={addCert} disabled={certUploading} className="btn-silver-3d self-end" data-testid="add-cert-btn">
              <Plus className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm ml-1">{certUploading ? "Adding…" : "Add"}</span>
            </button>
          </div>
          {certs.length > 0 && (
            <div className="flex flex-col gap-2 mt-3" data-testid="cert-list">
              {certs.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid={`cert-item-${c.id}`}>
                  <span className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-[#9b111e]" />
                    <span className="font-semibold">{c.name}</span>
                    {c.has_doc ? <span className="text-xs text-green-600">· document attached</span>
                      : c.reviewed ? <span className="text-xs text-slate-400">· verified</span>
                      : <span className="text-xs text-slate-400">· no file</span>}
                  </span>
                  <button onClick={() => removeCert(c.id)} data-testid={`remove-cert-${c.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              ))}
            </div>
          )}

          {/* resume / CV */}
          <h3 className="font-heading font-black mt-6 mb-1">Resume / CV <span className="text-slate-400 font-normal text-sm">(optional)</span></h3>
          <p className="text-sm text-slate-500 mb-3">
            Attach your resume as a <b>PDF</b> or a clear image. It's sent to the administrator with your application (never shown publicly).
          </p>
          <div className="flex items-center gap-3" data-testid="resume-block">
            <label className="btn-silver-3d !py-2 !px-4 cursor-pointer">
              <Upload className="w-4 h-4 text-[#9b111e]" />
              <span className="text-engraved text-sm">{resumeUploading ? "Uploading…" : user.has_resume ? "Replace Resume" : "Upload Resume"}</span>
              <input type="file" accept="application/pdf,image/*" className="hidden" data-testid="resume-upload-input"
                onChange={uploadResume} disabled={resumeUploading} />
            </label>
            {user.has_resume && (
              <span className="text-green-600 text-sm font-bold flex items-center gap-2" data-testid="resume-on-file">
                <ShieldCheck className="w-4 h-4" /> Resume on file ✓
                <button onClick={removeResume} data-testid="remove-resume-btn" className="text-red-600"><Trash2 className="w-4 h-4" /></button>
              </span>
            )}
          </div>

          {/* services builder */}
          <h3 className="font-heading font-black mt-6 mb-2">Your Services — set up each service with a description &amp; rate</h3>
          <div className="grid sm:grid-cols-4 gap-2 items-end">
            <div className="sm:col-span-2"><Label>Service name</Label><Input className={inputCls} data-testid="svc-name"
              value={newSvc.name} onChange={(e) => setNewSvc({ ...newSvc, name: e.target.value })} /></div>
            <div><Label>Category</Label>
              <Select value={newSvc.category} onValueChange={(v) => setNewSvc({ ...newSvc, category: v })}>
                <SelectTrigger className="mt-1 bg-white" data-testid="svc-category"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>{catNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Price $</Label><Input type="number" className={inputCls} data-testid="svc-price"
              value={newSvc.price} onChange={(e) => setNewSvc({ ...newSvc, price: e.target.value })} /></div>
          </div>
          <div className="grid sm:grid-cols-4 gap-2 items-end mt-2">
            <div className="sm:col-span-3"><Label>Service description</Label><Input className={inputCls} data-testid="svc-description"
              placeholder="What's included, how long it takes, what clients can expect…"
              value={newSvc.description} onChange={(e) => setNewSvc({ ...newSvc, description: e.target.value })} /></div>
            <button onClick={addService} className="btn-silver-3d self-end" data-testid="add-svc-btn"><Plus className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm ml-1">Add Service</span></button>
          </div>
          <div className="flex flex-col gap-2 mt-3">
            {services.map((s, i) => (
              <div key={i} className="flex items-start justify-between gap-2 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid={`svc-chip-${i}`}>
                <div>
                  <p className="font-semibold">{s.name} · ${s.price} <span className="text-slate-400 font-normal">· {s.category}</span></p>
                  {s.description && <p className="text-slate-500 text-xs mt-0.5">{s.description}</p>}
                </div>
                <button onClick={() => setServices(services.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-600" /></button>
              </div>
            ))}
          </div>

          {/* availability */}
          <h3 className="font-heading font-black mt-6 mb-1">Your Availability</h3>
          <p className="text-sm text-slate-500 mb-3">
            Choose the days and hours you'll accept bookings. Clients can only schedule you within these times.
          </p>
          <div className="space-y-2" data-testid="availability-editor">
            {WEEKDAYS.map((d) => (
              <div key={d.key} className="flex items-center gap-3 flex-wrap" data-testid={`avail-row-${d.key}`}>
                <label className="flex items-center gap-2 w-36 cursor-pointer">
                  <Checkbox checked={availability[d.key]?.enabled}
                    onCheckedChange={(v) => setDay(d.key, { enabled: !!v })} data-testid={`avail-${d.key}`} />
                  <span className="text-sm font-semibold">{d.label}</span>
                </label>
                {availability[d.key]?.enabled ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Input type="time" className="bg-white w-32" data-testid={`avail-${d.key}-start`}
                      value={availability[d.key].start} onChange={(e) => setDay(d.key, { start: e.target.value })} />
                    <span className="text-slate-400">to</span>
                    <Input type="time" className="bg-white w-32" data-testid={`avail-${d.key}-end`}
                      value={availability[d.key].end} onChange={(e) => setDay(d.key, { end: e.target.value })} />
                  </div>
                ) : <span className="text-sm text-slate-400">Unavailable</span>}
              </div>
            ))}
          </div>

          <div className="mt-6 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={contactConsent} onCheckedChange={setContactConsent} data-testid="consent-checkbox" className="mt-0.5" />
              <span>I give Care-4U.net permission to <b>text and call me about hiring</b>, including onboarding and scheduling.</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={interviewAvailable} onCheckedChange={setInterviewAvailable} data-testid="interview-checkbox" className="mt-0.5" />
              <span>I am <b>available to schedule a phone interview</b> with the administrator.</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={promise} onCheckedChange={setPromise} data-testid="promise-checkbox" className="mt-0.5" />
              <span>I promise to always be polite and wear a smile with clients.</span>
            </label>
            <label className="flex items-start gap-2 text-sm rounded-lg border-2 border-[#9b111e] bg-red-50 p-3" data-testid="no-bad-apples-pledge">
              <Checkbox checked={noBadApples} onCheckedChange={setNoBadApples} data-testid="no-bad-apples-checkbox" className="mt-0.5" />
              <span>
                <b className="text-[#9b111e]">NO BAD APPLES pledge:</b> I understand Care-4U.net runs an automatic
                accountability system — a review under 8 or a client-reported no-show earns a negative point, and
                <b> 3 negative points automatically moves me to Back-Up Pro status</b> (discounted rates). The comeback:
                <b> earn 3 perfect 10/10 reviews and I'm INSTANTLY restored to full status</b> (or 6 great reviews of 8+).
                I agree to <b>ALWAYS perform to the best of my abilities, putting customer
                service FIRST</b> and giving every client an extremely satisfying experience they'll want to repeat.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm rounded-lg border-2 border-[#9b111e] bg-red-50 p-3" data-testid="foot-covers-pledge">
              <Checkbox checked={footCovers} onCheckedChange={setFootCovers} data-testid="foot-covers-checkbox" className="mt-0.5" />
              <span>
                <b className="text-[#9b111e]">Foot Covers pledge:</b> I promise to <b>always wear foot covers (shoe covers)
                at every visit</b> to keep my clients' homes clean and show respect for their space.
              </span>
            </label>
          </div>
          <button onClick={submit} className="btn-red-3d w-full mt-4" data-testid="submit-application-btn">Submit Application</button>
        </div>
      )}

      {/* bookings */}
      {!mustApply && (
      <div>
        <PhoneVerifyCard />
        <NicknameCard />
        <div className="mb-6"><MessagesPanel /></div>
        {user.approved && <div className="mb-6"><SalesCard /></div>}
        <WeekSchedule bookings={bookings} />
        <h2 className="font-heading font-black text-xl mb-4 mt-8">Incoming Bookings</h2>
        {bookings.length === 0 && <p className="text-slate-500">No bookings yet.</p>}
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} className="card-metal p-4" data-testid={`pro-booking-${b.id}`}>
              <div className="flex justify-between">
                <div>
                  <p className="font-bold">{b.service} <span className="font-normal text-slate-400">for {b.client_nickname ? <>{b.client_nickname} <span className="text-slate-400">({b.client_name})</span></> : b.client_name}</span></p>
                  {(b.client_nickname || b.connected_since) && <p className="text-xs text-[#9b111e] font-semibold" data-testid={`nickname-greeting-${b.id}`}>You two are connected{b.client_nickname ? ` — say hi to ${b.client_nickname}! 😊` : ""}{b.connected_since ? ` · since ${new Date(b.connected_since).toLocaleDateString()}` : ""}</p>}
                  {(b.client_spotless || b.frequency || b.client_subscriber) && (
                    <p className="text-xs mt-0.5">
                      {b.client_subscriber && <span className="font-semibold text-purple-700 mr-2" data-testid={`subscriber-client-${b.id}`} title="Subscribe & Save member — one of your steadiest customers. Thank them!">💎 Subscriber</span>}
                      {b.client_spotless && <span className="font-semibold text-emerald-700 mr-2" data-testid={`spotless-client-${b.id}`} title="This client keeps a consistently spotless home">✨ Spotless Home</span>}
                      {b.frequency && <span className="font-semibold text-blue-700" data-testid={`recurring-pro-chip-${b.id}`} title="Part of a recurring series — the next visit books automatically after completion">🔁 Recurring · {b.frequency}</span>}
                    </p>
                  )}
                  <p className="text-sm text-slate-500">{new Date(b.scheduled_at).toLocaleString()}</p>
                  <p className="text-xs font-bold uppercase text-slate-500">{b.status} · {b.payment_status}</p>
                </div>
                <div className="text-right">
                  <p className="font-heading font-black text-green-700">${b.provider_payout}</p>
                  <p className="text-xs text-slate-400">after 5% fee</p>
                </div>
              </div>
              <div className="mt-3 border-t border-slate-100 pt-2">
                {b.home_report ? (
                  <p className="text-xs text-amber-700 flex items-center gap-1" data-testid={`home-report-status-${b.id}`}>
                    <AlertTriangle className="w-3.5 h-3.5" /> Private home report sent{typeof b.home_report.score === "number" ? ` · scored ${b.home_report.score}/10` : ""}{b.home_report.severity === "unsafe" ? " (unsafe)" : ""}{b.home_report.resolved ? " · reviewed by admin" : ""}
                  </p>
                ) : reportOpenId === b.id ? (
                  <div data-testid={`home-report-form-${b.id}`}>
                    <p className="text-xs text-slate-500 mb-1">Privately tell admin if this home wasn't safe or tidy. The client never sees this.</p>
                    <Textarea value={reportForm.issue} onChange={(e) => setReportForm((f) => ({ ...f, issue: e.target.value }))}
                      placeholder="e.g. Cluttered walkway, no clear space to work, or a safety concern."
                      className="text-sm" data-testid={`home-report-input-${b.id}`} />
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <select className="border border-slate-300 rounded-md p-1.5 text-xs bg-white"
                        value={reportForm.severity} onChange={(e) => setReportForm((f) => ({ ...f, severity: e.target.value }))}
                        data-testid={`home-report-severity-${b.id}`}>
                        <option value="minor">Not tidy / minor</option>
                        <option value="unsafe">Unsafe — urgent</option>
                      </select>
                      <label className="text-xs text-slate-600 flex items-center gap-1">Home score
                        <select className="border border-slate-300 rounded-md p-1.5 text-xs bg-white"
                          value={reportForm.score} onChange={(e) => setReportForm((f) => ({ ...f, score: e.target.value }))}
                          data-testid={`home-report-score-${b.id}`}>
                          {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}/10</option>)}
                        </select>
                      </label>
                      <button className="btn-metal text-xs px-4 py-1.5" onClick={() => submitHomeReport(b.id)} data-testid={`home-report-submit-${b.id}`}>Send privately</button>
                      <button className="text-xs text-slate-500 px-2" onClick={() => { setReportOpenId(null); setReportForm({ issue: "", severity: "minor", score: "6" }); }}>Cancel</button>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">Scores below 7/10 count toward the client's Hazardous Home status.</p>
                  </div>
                ) : (
                  <button className="text-xs text-slate-500 hover:text-amber-700 flex items-center gap-1 transition-colors"
                    onClick={() => { setReportOpenId(b.id); setReportForm({ issue: "", severity: "minor", score: "6" }); }}
                    data-testid={`home-report-btn-${b.id}`}>
                    <AlertTriangle className="w-3.5 h-3.5" /> Report unsafe / untidy home
                  </button>
                )}
                {(b.service || "").toLowerCase().includes("clean") && (
                  b.clean_report ? (
                    <p className="text-xs text-green-700 flex items-center gap-1 mt-2" data-testid={`clean-report-status-${b.id}`}>
                      ✨ Clean report sent to admin ({b.clean_report.photos?.length || 0} photo{(b.clean_report.photos?.length || 0) === 1 ? "" : "s"}){b.clean_report.status === "approved" ? " · verified by admin" : " · awaiting admin review"}
                    </p>
                  ) : cleanOpenId === b.id ? (
                    <div className="mt-2 bg-green-50 border border-green-200 rounded p-2" data-testid={`clean-report-form-${b.id}`}>
                      <p className="text-xs text-slate-600 mb-1">Report to the administrator that this home is now <b>straight and CLEAN (10/10)</b>. Upload 1–5 photos of the neat home as proof.</p>
                      <Textarea value={cleanNote} onChange={(e) => setCleanNote(e.target.value)}
                        placeholder="Optional note — e.g. Deep-cleaned every room, walkways fully clear."
                        className="text-sm bg-white" data-testid={`clean-report-note-${b.id}`} />
                      <input type="file" accept="image/*" multiple className="text-xs mt-2 block"
                        onChange={(e) => setCleanFiles(Array.from(e.target.files || []))}
                        data-testid={`clean-report-photos-${b.id}`} />
                      {cleanFiles.length > 0 && <p className="text-[10px] text-slate-500 mt-1">{cleanFiles.length} photo(s) selected</p>}
                      <div className="flex items-center gap-2 mt-2">
                        <button className="btn-metal text-xs px-4 py-1.5" onClick={() => submitCleanReport(b.id)} data-testid={`clean-report-submit-${b.id}`}>Send clean report</button>
                        <button className="text-xs text-slate-500 px-2" onClick={() => { setCleanOpenId(null); setCleanNote(""); setCleanFiles([]); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button className="text-xs text-green-700 hover:text-green-800 flex items-center gap-1 transition-colors mt-2"
                      onClick={() => { setCleanOpenId(b.id); setCleanNote(""); setCleanFiles([]); }}
                      data-testid={`clean-report-btn-${b.id}`}>
                      ✨ Report home CLEAN (10/10) with photos
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}
